import { translateUi } from "@/shared/lib/ui-translate";
import { z } from "zod";

/** Бэкенд: JsonStringEnumConverter → \"None\" | \"AnimatedGradient\" | \"Glow\" | \"Shine\" */
export const proNicknameStyleSchema = z.lazy(() =>
  z.preprocess((val: unknown) => {
  if (val === undefined || val === null) return undefined;
  if (typeof val === "number" && Number.isFinite(val)) return Math.trunc(val);
  if (typeof val === "string") {
    const key = val.trim().toLowerCase();
    const byLower: Record<string, number> = {
      none: 0,
      animatedgradient: 1,
      glow: 2,
      shine: 3,
    };
    if (key in byLower) return byLower[key];
    const n = Number(val);
    if (!Number.isNaN(n)) return n;
  }
  return undefined;
}, z.number().int().min(0).max(3).optional()));

/**
 * @deprecated Используйте goodLuckCoreDTOsUserProfileDtoSchema из generated-kubb/schemas
 */
export const UserProfileSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litUserSchemas.idNeMozhetBytPustym") }),
  username: z
    .string()
    .min(3, { error: () => translateUi("litUserSchemas.imyaPolzovatelyaDolzhnoSoderzhatMinimum3") })
    .max(50, { error: () => translateUi("litUserSchemas.imyaPolzovatelyaNeMozhetBytDlinnee") }),
  displayName: z
    .string()
    .min(1, { error: () => translateUi("litUserSchemas.otobrazhaemoeImyaNeMozhetBytPustym") })
    .max(50, { error: () => translateUi("litUserSchemas.otobrazhaemoeImyaNeMozhetBytDlinnee") }),
  isVerified: z.boolean().default(false),
  platformRoles: z.array(z.string()).default([]),
  primaryPlatformRole: z.string().default("User"),
  isOnline: z.boolean().default(false),
  status: z
    .enum(["online", "offline", "playing", "busy", "away", "searching", "invisible"])
    .default("offline"),
  avatar: z
    .string()
    .nullable()
    .transform((v) => v ?? "")
    .default(""),
  banner: z
    .string()
    .nullable()
    .transform((v) => (v === null || v === "null" ? "" : v))
    .default(""),
  about: z
    .string()
    .nullable()
    .transform((v) => v ?? "")
    .default(""),
  location: z
    .string()
    .nullable()
    .transform((v) => v ?? "")
    .default(""),
  languages: z.array(z.string()).default([]),
  website: z
    .string()
    .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrl") })
    .nullable()
    .transform((v) => v ?? "")
    .default(""),
  isAdmin: z.boolean().optional().default(false),
  isSiteAdmin: z.boolean().optional().default(false),
  socialLinks: z
    .object({
      x: z
        .string()
        .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlDlyaX") })
        .nullable()
        .default(null)
        .optional(),
      discord: z
        .string()
        .regex(/^[a-zA-Z0-9._-]+$/, { error: () => translateUi("litUserSchemas.nekorrektnyyDiscordUsername") })
        .nullable()
        .default(null)
        .optional(),
      youtube: z
        .string()
        .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlDlyaYoutube") })
        .nullable()
        .default(null)
        .optional(),
      telegram: z
        .string()
        .regex(/^@[a-zA-Z0-9_]+$/, { error: () => translateUi("litUserSchemas.nekorrektnyyTelegramUsername") })
        .nullable()
        .default(null)
        .optional(),
      twitch: z
        .string()
        .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlDlyaTwitch") })
        .nullable()
        .default(null)
        .optional(),
      steam: z
        .string()
        .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlDlyaSteam") })
        .nullable()
        .default(null)
        .optional(),
      vk: z
        .string()
        .url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlDlyaVk") })
        .nullable()
        .default(null)
        .optional(),
    })
    .default({}),
  stats: z
    .object({
      totalTournaments: z
        .number()
        .min(0, { error: () => translateUi("litUserSchemas.kolichestvoTurnirovNeMozhetBytOtritsatelnym") })
        .default(0),
      wins: z
        .number()
        .min(0, { error: () => translateUi("litUserSchemas.kolichestvoPobedNeMozhetBytOtritsatelnym") })
        .default(0),
      winrate: z
        .number()
        .min(0, { error: () => translateUi("litUserSchemas.protsentPobedNeMozhetBytOtritsatelnym") })
        .max(100, { error: () => translateUi("litUserSchemas.protsentPobedNeMozhetBytBolshe") })
        .default(0),
      kda: z
        .string()
        .regex(
          /^\d+\/\d+\/\d+$/,
          { error: () => translateUi("litUserSchemas.kdaDolzhenBytVFormateKills") },
        )
        .default("0/0/0"),
      rank: z.number().min(0, { error: () => translateUi("litUserSchemas.rangNeMozhetBytOtritsatelnym") }).default(0),
    })
    .default({
      totalTournaments: 0,
      wins: 0,
      winrate: 0,
      kda: "0/0/0",
      rank: 0,
    }),
  privacy: z
    .object({
      showStats: z.boolean().default(true),
      showOnlineStatus: z.boolean().default(true),
      allowMessages: z.boolean().default(true),
      showInSearch: z.boolean().default(true),
    })
    .default({
      showStats: true,
      showOnlineStatus: true,
      allowMessages: true,
      showInSearch: true,
    }),
  notifications: z
    .object({
      email: z.boolean().default(true),
      tournamentStart: z.boolean().default(true),
      // KAN-1362: новости — только явное согласие.
      newsAndRecommendations: z.boolean().default(false),
      emailTeams: z.boolean().default(true),
      discordEnabled: z.boolean().default(false),
      discordSendAll: z.boolean().default(false),
      discordTypes: z.array(z.string()).default([]),
    })
    .default({
      email: true,
      tournamentStart: true,
      newsAndRecommendations: false,
      emailTeams: true,
      discordEnabled: false,
      discordSendAll: false,
      discordTypes: [],
    }),
  achievements: z
    .array(
      z.object({
        icon: z.string(),
        title: z.string(),
        description: z.string(),
        rarity: z.enum(["Common", "Uncommon", "Rare", "Epic", "Legendary"]).optional(),
        earnedAt: z.string().optional(),
      }),
    )
    .default([]),
  favoriteChampions: z
    .array(
      z.object({
        name: z.string(),
        image: z.string(),
        games: z.number(),
        winrate: z.number(),
      }),
    )
    .default([]),
  teams: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        tag: z.string(),
        icon: z.string(),
        role: z.string(),
        status: z.string(),
        joinedAt: z.string(),
      }),
    )
    .default([]),
  organizations: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        url: z.string(),
        avatarUrl: z.string().optional(),
        role: z.string(),
        joinedAt: z.string().optional(),
      }),
    )
    .optional(),
  riotAccounts: z
    .array(
      z.object({
        id: z.string(),
        gameName: z.string(),
        tagLine: z.string(),
        region: z.enum([
          "EUW",
          "EUNE",
          "NA",
          "BR",
          "LAN",
          "LAS",
          "OCE",
          "RU",
          "TR",
          "JP",
          "KR",
          "EUW1",
          "EUNE1",
          "NA1",
          "BR1",
          "LAN1",
          "LAS1",
          "OC1",
          "RU1",
          "TR1",
          "JP1",
          "KR1",
        ]),
        isMain: z.boolean().optional().default(false),
        verified: z.boolean().optional().default(false),
        avatar: z.string().url().optional(),
        profileIconId: z.number().optional(),
        rank: z.string().optional(),
      }),
    )
    .default([]),
  championMasteries: z
    .array(
      z.object({
        championId: z.number(),
        championName: z.string(),
        championLevel: z.number(),
        championPoints: z.number(),
        lastPlayTime: z.string().or(z.date()),
      }),
    )
    .default([]),
  streams: z
    .array(
      z.object({
        title: z.string(),
        viewers: z.number(),
        game: z.string(),
        streamer: z.string(),
        isOnline: z.boolean(),
      }),
    )
    .default([]),
}));

/**
 * @deprecated Используйте UserProfile из shared/api/user (z.infer<typeof goodLuckCoreDTOsUserProfileDtoSchema>)
 */
export type UserProfileDTO = z.infer<typeof UserProfileSchema>;

export const RiotAccountSchema = z.object({
  id: z.string(),
  gameName: z.string(),
  tagLine: z.string(),
  region: z.enum([
    "EUW",
    "EUNE",
    "NA",
    "BR",
    "LAN",
    "LAS",
    "OCE",
    "RU",
    "TR",
    "JP",
    "KR",
    "EUW1",
    "EUNE1",
    "NA1",
    "BR1",
    "LAN1",
    "LAS1",
    "OC1",
    "RU1",
    "TR1",
    "JP1",
    "KR1",
  ]),
  verified: z.boolean().optional().default(false),
  avatar: z.string().url().optional().default(""),
  profileIconId: z.number().int().optional().default(0),
  isMain: z.boolean().optional().default(false),
  rank: z.string().optional(),
});

export const UserSearchResultSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litUserSchemas.idNeMozhetBytPustym") }),
  username: z
    .string()
    .min(3, { error: () => translateUi("litUserSchemas.imyaPolzovatelyaDolzhnoSoderzhatMinimum3") }),
  displayName: z.string().min(1, { error: () => translateUi("litUserSchemas.otobrazhaemoeImyaNeMozhetBytPustym") }),
  avatarUrl: z.string().optional().nullable(),
  status: z
    .enum(["online", "offline", "playing", "busy", "away", "searching", "invisible"])
    .optional()
    .catch(undefined), // неизвестный статус не должен ронять весь поиск (KAN-1251)
  bannerUrl: z.string().optional().nullable(),
  primaryPlatformRole: z.string().optional().default("User"),
  hasPro: z.boolean().optional(),
  proNicknameStyle: proNicknameStyleSchema,
  proNicknameColorA: z.string().nullable().optional(),
  proNicknameColorB: z.string().nullable().optional(),
}));

export const UserTeamSchema = z.lazy(() =>
  z.object({
  id: z.string().min(1, { error: () => translateUi("litUserSchemas.idKomandyNeMozhetBytPustym") }),
  name: z
    .string()
    .min(3, { error: () => translateUi("litUserSchemas.nazvanieKomandyDolzhnoSoderzhatMinimum3") })
    .max(30, { error: () => translateUi("litUserSchemas.nazvanieKomandyNeMozhetBytDlinnee") }),
  tag: z
    .string()
    .min(2, { error: () => translateUi("litUserSchemas.tegKomandyDolzhenSoderzhatMinimum2") })
    .max(5, { error: () => translateUi("litUserSchemas.tegKomandyNeMozhetBytDlinnee") }),
  logoUrl: z.string().url({ error: () => translateUi("litUserSchemas.nekorrektnyyUrlLogotipa") }).nullable().optional(),
  icon: z.string().default(""),
  captainId: z.string().min(1, { error: () => translateUi("litUserSchemas.idKapitanaNeMozhetBytPustym") }),
  memberCount: z
    .number()
    .min(0, { error: () => translateUi("litUserSchemas.kolichestvoUchastnikovNeMozhetBytOtritsatelnym") }),
  maxMembers: z
    .number()
    .min(1, { error: () => translateUi("litUserSchemas.maksimalnoeKolichestvoUchastnikovDolzhnoBytBolshe") }),
  createdAt: z
    .string()
    .or(z.date())
    .transform((val) => {
      if (val instanceof Date) {
        return val.toISOString();
      }
      return typeof val === "string" ? val : new Date(val).toISOString();
    }),
  userRole: z.string().min(1, { error: () => translateUi("litUserSchemas.rolPolzovatelyaNeMozhetBytPustoy") }),
  /** Временная команда, созданная под турнир (solo/duo-registered) */
  isTemporary: z.boolean().default(false),
  /** ID турнира, к которому привязана временная команда; null для постоянных */
  tournamentId: z.string().nullable().optional(),
  /** Дата архивации ростера; null — команда активна */
  archivedAt: z.string().nullable().optional(),
  /** Может ли пользователь управлять флагом isLookingForPartner */
  canManageLft: z.boolean().default(false),
}));
